import { useState, useEffect } from "react";
import { social, getPlatformInfo, type SocialAccount, type SocialPost } from "../lib/social";
import { navigate } from "../lib/router";
import { useStaticData } from "../lib/static-data";
import { PlatformIcon } from "../components/PlatformIcon";
import { useAccounts } from "../lib/app-data";
import { DashboardSkeleton } from "../components/Skeletons";

export function Dashboard() {
  const ssrPosts = useStaticData<SocialPost[]>("posts");
  const { accounts, loading: accountsLoading } = useAccounts();
  const [posts, setPosts] = useState<SocialPost[]>(ssrPosts || []);
  const [postsLoading, setPostsLoading] = useState(!ssrPosts);

  useEffect(() => {
    if (ssrPosts) return;
    social
      .listPosts({ sort: "created_at:desc", limit: 5 })
      .then((res) => setPosts((res as any).data || []))
      .catch(() => {})
      .finally(() => setPostsLoading(false));
  }, [ssrPosts]);

  const activeAccounts = accounts.filter((a) => a.status === "active");
  const loading = accountsLoading || postsLoading;

  if (loading) return <DashboardSkeleton />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight">Dashboard</h2>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">Overview of your social presence</p>
        </div>
        <button
          onClick={() => navigate("/compose")}
          className="px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-all"
        >
          New Post
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
          <div className="text-3xl font-bold text-stone-900 dark:text-white">{activeAccounts.length}</div>
          <div className="text-xs text-stone-500 dark:text-zinc-500 mt-1 font-medium uppercase tracking-wide">Connected</div>
        </div>
        <div className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
          <div className="text-3xl font-bold text-stone-900 dark:text-white">{posts.length}</div>
          <div className="text-xs text-stone-500 dark:text-zinc-500 mt-1 font-medium uppercase tracking-wide">Recent Posts</div>
        </div>
        <div className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
          <div className="text-3xl font-bold text-stone-900 dark:text-white">
            {new Set(activeAccounts.map((a) => a.platform)).size}
          </div>
          <div className="text-xs text-stone-500 dark:text-zinc-500 mt-1 font-medium uppercase tracking-wide">Platforms</div>
        </div>
      </div>

      {/* Connected accounts */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-900 dark:text-white uppercase tracking-wide">Accounts</h3>
          <button onClick={() => navigate("/accounts")} className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
            Manage
          </button>
        </div>
        {activeAccounts.length === 0 ? (
          <div className="p-8 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
            <p className="text-stone-500 dark:text-zinc-500 text-sm mb-3">No accounts connected yet</p>
            <button onClick={() => navigate("/accounts")} className="text-orange-600 dark:text-orange-400 text-sm hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
              Connect your first account
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {activeAccounts.map((account) => {
              const info = getPlatformInfo(account.platform);
              return (
                <div
                  key={account.id}
                  className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white dark:bg-zinc-900 rounded-xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-stone-300 dark:hover:border-zinc-700 transition-colors"
                >
                  <span className={`w-7 h-7 ${info.color} text-white rounded-lg flex items-center justify-center`}>
                    <PlatformIcon platform={account.platform} className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sm font-medium text-stone-700 dark:text-zinc-300">
                    {account.platform_display_name || account.platform_username || info.name}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent posts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-900 dark:text-white uppercase tracking-wide">Recent Posts</h3>
          <button onClick={() => navigate("/posts")} className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
            View all
          </button>
        </div>
        {posts.length === 0 ? (
          <div className="p-8 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
            <p className="text-stone-500 dark:text-zinc-500 text-sm mb-3">No posts yet</p>
            <button onClick={() => navigate("/compose")} className="text-orange-600 dark:text-orange-400 text-sm hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
              Create your first post
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.id}
                className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-stone-300 dark:hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                    post.status === "published"
                      ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
                      : post.status === "partial"
                      ? "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
                      : post.status === "failed"
                      ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400"
                      : "bg-stone-100 text-stone-500 dark:bg-zinc-800 dark:text-zinc-500"
                  }`}>
                    {post.status}
                  </span>
                  <span className="text-xs text-stone-400 dark:text-zinc-600">
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-stone-700 dark:text-zinc-300 line-clamp-2">
                  {post.text || "No text"}
                </p>
                {post.total_accounts > 0 && (
                  <p className="text-xs text-stone-400 dark:text-zinc-600 mt-2">
                    {post.successful_count}/{post.total_accounts} succeeded
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
